import React, { useEffect, useContext, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import Exercises from "./components/exercises/Exercises";
import VocabularyExercises from "./components/exercises/vocabulary/VocabularyExercises";
import ReadingExercises from "./components/exercises/reading/ReadingExercises";
import UserVocabulary from "./components/vocabulary/UserVacabulary";
import VocabularyDashboard from "./components/vocabulary/VocabularyDashboard";
import SetWords from "./components/vocabulary/SetWords";
import UserPageWrapper from "./components/user/UserPageWrapper";
import SignIn from "./components/auth/SignIn";
import SignUp from "./components/auth/SignUp";
import Sidebar from "./components/sidebar/Sidebar";
import AllSets from "./components/vocabulary/AllSets";
import { Table, BookHalf, JournalText } from "react-bootstrap-icons";
import { signOut as amplifySignOut, getCurrentUser } from "aws-amplify/auth";
import { UserProvider, UserContext } from "./components/user/UserContext";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSignedIn,
  setUser,
  clearUserState,
} from "./redux/slices/authSlice";

import "./App.css";

const navItems = [
  {
    name: "VocabularyDashboard",
    label: "Vocabulary",
    icon: JournalText,
    path: "/vocabularyDashboard",
    component: VocabularyDashboard,
  },
  {
    name: "Exercises",
    label: "Exercises",
    icon: Table,
    // path: "/exercises",
    component: Exercises,
  },
  // {
  //   name: "Home",
  //   label: "Home",
  //   icon: House,
  //   path: "/",
  //   component: HomePage,
  // },
];

function App() {
  const navigate = useNavigate();
  const { clearUserAttributes } = useContext(UserContext);
  const dispatch = useDispatch();

  // Accessing the state from Redux
  const isSignedIn = useSelector((state) => state.auth.isSignedIn);
  const user = useSelector((state) => state.auth.user);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);

  useEffect(() => {
    async function checkUserStatus() {
      try {
        const currentUser = await getCurrentUser();
        dispatch(setIsSignedIn(true));
        dispatch(setUser(currentUser));
      } catch (error) {
        dispatch(setIsSignedIn(false));
        dispatch(clearUserState());
      }
    }

    checkUserStatus();
  }, [dispatch]);

  useEffect(() => {
    // Function to update the height of the bottom bar
    const updateBottomBarHeight = () => {
      const bottomBar = document.getElementById("bottom-bar");
      if (bottomBar) {
        const newHeight = bottomBar.offsetHeight;
        setBottomBarHeight(newHeight);
      } else {
        console.log("Bottom bar not found during height update");
      }
    };

    // Use setTimeout to defer the execution
    const timeoutId = setTimeout(() => {
      const bottomBar = document.getElementById("bottom-bar");
      if (!bottomBar) {
        return;
      }
      const resizeObserver = new ResizeObserver(() => {
        updateBottomBarHeight();
      });
      // Start observing the bottom bar
      resizeObserver.observe(bottomBar);
      // Set initial height
      updateBottomBarHeight();
    }, 300); // Adjust timeout duration as needed

    // Cleanup the observer and timeout when the component is unmounted
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  async function handleSignOut() {
    try {
      clearUserAttributes();
      await amplifySignOut();
      dispatch(setIsSignedIn(false));
      dispatch(clearUserState());
      navigate("/signin");
    } catch (error) {
      console.log("error signing out: ", error);
    }
  }

  // Wrapper component to extract the setName from the URL and pass it to SetWords
  function SetWordsWrapper() {
    const { setName } = useParams();
    return <SetWords setName={decodeURIComponent(setName)} />;
  }

  if (!isSignedIn) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }

  return (
    <UserProvider>
      <div className="App d-flex">
        <div className="d-md-none fixed-top bg-body-tertiary p-2 text-center">
          <a
            href="/"
            className="d-flex align-items-center justify-content-center text-decoration-none"
          >
            <BookHalf width="40" height="40" />
            <span className="fs-4 ms-5">Fluent Serbian</span>
          </a>
        </div>
        <Sidebar navItems={navItems} signOut={handleSignOut} />
        <div
          id="main-content"
          className="container-fluid pt-md-0 pt-sm"
          style={{
            paddingBottom: `${bottomBarHeight}px`,
          }}
        >
          <Routes>
            {navItems.map((item) => (
              <Route
                key={item.name}
                path={item.path}
                element={<item.component signOut={handleSignOut} user={user} />}
              />
            ))}
            <Route
              path="/user"
              element={<UserPageWrapper signOut={handleSignOut} user={user} />}
            />
            <Route
              path="/vocabulary"
              element={<UserVocabulary signOut={handleSignOut} user={user} />}
            />
            <Route
              path="/exercises/vocabulary"
              element={
                <VocabularyExercises signOut={handleSignOut} user={user} />
              }
            />
            <Route
              path="/exercises/reading"
              element={<ReadingExercises signOut={handleSignOut} user={user} />}
            />
            <Route path="/vocabulary/:setName" element={<SetWordsWrapper />} />
            <Route path="/allsets" element={<AllSets />} />
          </Routes>
        </div>
      </div>
    </UserProvider>
  );
}

export default App;
